import httpClient from './httpClient';
import { getUserData } from '../utils/auth';

class ProductsService {
  // Get all products with filters and pagination
  async getProducts(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      // Add search parameter
      if (params.search) {
        queryParams.append('search', params.search);
      }

      // Add companies filter (array)
      if (params.companies && Array.isArray(params.companies) && params.companies.length > 0) {
        params.companies.forEach(companyId => {
          queryParams.append('companies[]', companyId);
        });
      }

      // Add sub_categories filter (array)
      if (params.sub_categories && Array.isArray(params.sub_categories) && params.sub_categories.length > 0) {
        params.sub_categories.forEach(categoryId => {
          queryParams.append('sub_categories[]', categoryId);
        });
      }

      // Add colors filter (array)
      if (params.colors && Array.isArray(params.colors) && params.colors.length > 0) {
        params.colors.forEach(colorId => {
          queryParams.append('colors[]', colorId);
        });
      }

      // Add genders filter
      if (params.genders && Array.isArray(params.genders) && params.genders.length > 0) {
        params.genders.forEach(gender => {
          queryParams.append('genders[]', gender);
        });
      }

      // Add price range filters
      if (params.min_price) {
        queryParams.append('min_price', params.min_price);
      }

      if (params.max_price) {
        queryParams.append('max_price', params.max_price);
      }

      // Add sale filter
      if (params.is_on_sale !== undefined && params.is_on_sale !== null) {
        queryParams.append('is_on_sale', params.is_on_sale ? 1 : 0);
      }

      // Add pagination parameters
      if (params.per_page) {
        queryParams.append('per_page', params.per_page);
      }

      if (params.page) {
        queryParams.append('page', params.page);
      }

      // Add sorting parameters
      if (params.sort_by) {
        queryParams.append('sort_by', params.sort_by);
      }

      if (params.sort_direction) {
        queryParams.append('sort_direction', params.sort_direction);
      }

      // Add sizes filter (array)
      if (params.sizes && Array.isArray(params.sizes) && params.sizes.length > 0) {
        params.sizes.forEach(sizeId => {
          queryParams.append('sizes[]', sizeId);
        });
      }

      const queryString = queryParams.toString();
      // Use /company endpoints for company users
      let base = '/api/admin/products';
      try {
        const u = getUserData();
        if (u && (u.logo || typeof u.user_id !== 'undefined')) {
          base = '/api/company/products';
        }
      } catch {}
      const url = `${base}${queryString ? `?${queryString}` : ''}`;

      console.log('ProductsService: Making request to', url);
      const response = await httpClient.get(url);
      console.log('ProductsService: Raw response from httpClient:', response);
      console.log('ProductsService: Response type:', typeof response);
      console.log('ProductsService: Response keys:', Object.keys(response || {}));
      console.log('ProductsService: Response.data type:', typeof response?.data);
      console.log('ProductsService: Response.data keys:', Object.keys(response?.data || {}));

      return response;
    } catch (error) {
      console.error('ProductsService: Error in getProducts:', error);
      throw error; // Throw the error so the component can handle it
    }
  }

  // Get product by ID
  async getProductById(id) {
    try {
      console.log('ProductsService: Making request to get product by ID:', id);
      let base = '/api/admin/products/';
      try {
        const u = getUserData();
        if (u && (u.logo || typeof u.user_id !== 'undefined')) base = '/api/company/products/';
      } catch {}
      const response = await httpClient.get(`${base}${id}`);
      console.log('ProductsService: Raw response from httpClient:', response.data);
      
      return response;
    } catch (error) {
      console.error('ProductsService: Error in getProductById:', error);
      throw error; // Throw the error so the component can handle it
    }
  }

  // Create new product
  async createProduct(productData) {
    try {
      console.log('ProductsService: Creating new product:', productData);
      const formData = new FormData();
      formData.append('name', productData.name);
      formData.append('description', productData.description);
      formData.append('gender', productData.gender);
      formData.append('is_on_sale', productData.is_on_sale ? 1 : 0);
      formData.append('is_replaceable', productData.is_replaceable ? 1 : 0);
      formData.append('is_refundable', productData.is_refundable ? 1 : 0);
      formData.append('points', productData.points || 0);
      formData.append('company_id', productData.company_id);

      if (productData.categories && Array.isArray(productData.categories)) {
        productData.categories.forEach(categoryId => formData.append('categories[]', categoryId));
      }

      if (productData.branches && Array.isArray(productData.branches)) {
        productData.branches.forEach((branch, bi) => {
          if (branch.branch_id != null) formData.append(`branches[${bi}][branch_id]`, branch.branch_id);
          if (branch.price != null) formData.append(`branches[${bi}][price]`, branch.price);
          if (branch.colors && Array.isArray(branch.colors)) {
            branch.colors.forEach((color, ci) => {
              if (color.color_id != null) formData.append(`branches[${bi}][colors][${ci}][color_id]`, color.color_id);
              if (color.sizes && Array.isArray(color.sizes)) {
                color.sizes.forEach((size, si) => {
                  if (size.size_id != null) formData.append(`branches[${bi}][colors][${ci}][sizes][${si}][size_id]`, size.size_id);
                  if (size.quantity != null) formData.append(`branches[${bi}][colors][${ci}][sizes][${si}][quantity]`, size.quantity);
                });
              }
            });
          }
        });
      }

      if (productData.images && Array.isArray(productData.images)) {
        productData.images.forEach((image) => {
          if (image instanceof File || image instanceof Blob) {
            const filename = image.name || `image-${Math.random().toString(36).slice(2)}.jpg`;
            formData.append('images[]', image, filename);
          }
        });
      }

      let endpoint = '/api/admin/products';
      try {
        const u = getUserData();
        if (u && (u.logo || typeof u.user_id !== 'undefined')) endpoint = '/api/company/products';
      } catch {}
      const response = await httpClient.post(endpoint, formData);
      console.log('ProductsService: Raw response from httpClient:', response);
      
      return response;
    } catch (error) {
      console.error('ProductsService: Error in createProduct:', error);
      throw error; // Throw the error so the component can handle it
    }
  }

  // Update product
  async updateProduct(id, productData) {
    try {
      console.log('ProductsService: Updating product:', id, productData);
      
      let requestData;
      const shouldUseFormData = true; // we always send images[] files in order
      
      if (shouldUseFormData) {
        const formData = new FormData();
        formData.append('name', productData.name);
        formData.append('description', productData.description);
        formData.append('gender', productData.gender);
        formData.append('is_on_sale', productData.is_on_sale ? 1 : 0);
        formData.append('is_replaceable', productData.is_replaceable ? 1 : 0);
        formData.append('is_refundable', productData.is_refundable ? 1 : 0);
        formData.append('points', productData.points || 0);
        formData.append('company_id', productData.company_id);
        
        // Add categories as array
        if (productData.categories && Array.isArray(productData.categories)) {
          productData.categories.forEach(categoryId => {
            formData.append('categories[]', categoryId);
          });
        }

        // Add branches nested structure
        if (productData.branches && Array.isArray(productData.branches)) {
          productData.branches.forEach((branch, bi) => {
            if (branch.branch_id != null) formData.append(`branches[${bi}][branch_id]`, branch.branch_id);
            if (branch.price != null) formData.append(`branches[${bi}][price]`, branch.price);
            if (branch.colors && Array.isArray(branch.colors)) {
              branch.colors.forEach((color, ci) => {
                if (color.color_id != null) formData.append(`branches[${bi}][colors][${ci}][color_id]`, color.color_id);
                if (color.sizes && Array.isArray(color.sizes)) {
                  color.sizes.forEach((size, si) => {
                    if (size.size_id != null) formData.append(`branches[${bi}][colors][${ci}][sizes][${si}][size_id]`, size.size_id);
                    if (size.quantity != null) formData.append(`branches[${bi}][colors][${ci}][sizes][${si}][quantity]`, size.quantity);
                  });
                }
              });
            }
          });
        }
        
        // Add images as files only, in final order
        if (productData.images && Array.isArray(productData.images)) {
          productData.images.forEach((image) => {
            if (image instanceof File || image instanceof Blob) {
              const filename = image.name || `image-${Math.random().toString(36).slice(2)}.jpg`;
              formData.append('images[]', image, filename);
            }
          });
        }
        
        requestData = formData;
        console.log('ProductsService: Updating with FormData');
      } else {
        // Never used: backend requires files; kept for completeness
        requestData = { ...productData };
      }

      let endpoint = `/api/admin/products/${id}`;
      try {
        const u = getUserData();
        if (u && (u.logo || typeof u.user_id !== 'undefined')) endpoint = `/api/company/products/${id}`;
      } catch {}
      const response = await httpClient.post(endpoint, requestData);
      return response;
    } catch (error) {
      console.error('ProductsService: Error in updateProduct:', error);
      throw error; // Throw the error so the component can handle it
    }
  }

  // Delete product
  async deleteProduct(id) {
    try {
      console.log('ProductsService: Deleting product:', id);
      let endpoint = `/api/admin/products/${id}`;
      try {
        const u = getUserData();
        if (u && (u.logo || typeof u.user_id !== 'undefined')) endpoint = `/api/company/products/${id}`;
      } catch {}
      const response = await httpClient.delete(endpoint);
      console.log('ProductsService: Raw response from httpClient:', response);
      
      return response;
    } catch (error) {
      console.error('ProductsService: Error in deleteProduct:', error);
      throw error; // Throw the error so the component can handle it
    }
  }

  // Update a sale entry
  async updateSale(id, saleData) {
    try {
      console.log('ProductsService: Updating sale:', id, saleData);
      let endpoint = `/api/admin/product-sales/${id}`;
      try {
        const u = getUserData();
        if (u && (u.logo || typeof u.user_id !== 'undefined')) endpoint = `/api/company/product-sales/${id}`;
      } catch {}
      const response = await httpClient.post(endpoint, saleData);
      return response;
    } catch (error) {
      console.error('ProductsService: Error in updateSale:', error);
      throw error;
    }
  }

  // Delete a sale entry
  async deleteSale(id) {
    try {
      console.log('ProductsService: Deleting sale:', id);
      let endpoint = `/api/admin/product-sales/${id}`;
      try {
        const u = getUserData();
        if (u && (u.logo || typeof u.user_id !== 'undefined')) endpoint = `/api/company/product-sales/${id}`;
      } catch {}
      const response = await httpClient.delete(endpoint);
      return response;
    } catch (error) {
      console.error('ProductsService: Error in deleteSale:', error);
      throw error;
    }
  }

  // Create product sales for multiple products
  async createProductSales(data) {
    try {
      // Expecting: { product_ids: number[], discount_type, discount_amount, start_date, end_date }
      const formData = new FormData();
      (data.product_ids || []).forEach(id => formData.append('product_ids[]', id));
      formData.append('discount_type', data.discount_type);
      formData.append('discount_amount', data.discount_amount);
      if (data.start_date) formData.append('start_date', data.start_date);
      if (data.end_date) formData.append('end_date', data.end_date);
      let endpoint = '/api/admin/product-sales';
      try {
        const u = getUserData();
        if (u && (u.logo || typeof u.user_id !== 'undefined')) endpoint = '/api/company/product-sales';
      } catch {}
      const response = await httpClient.post(endpoint, formData);
      return response;
    } catch (error) {
      console.error('ProductsService: Error in createProductSales:', error);
      throw error;
    }
  }

  // Get filters data (genders, colors, sizes, companies, sub_categories, price_range)
  async getFilters() {
    try {
      let endpoint = '/api/admin/products/filters';
      try {
        const u = getUserData();
        if (u && (u.logo || typeof u.user_id !== 'undefined')) endpoint = '/api/company/products/filters';
      } catch {}
      const response = await httpClient.get(endpoint);
      return response;
    } catch (error) {
      console.error('ProductsService: Error in getFilters:', error);
      throw error;
    }
  }

  // Get company branches by company_id (no /api prefix as per backend route)
  async getCompanyBranches(companyId) {
    let base = '/api/admin/company-branches/list';
    try {
      const u = getUserData();
      if (u && (u.logo || typeof u.user_id !== 'undefined')) base = '/api/company/company-branches/list';
    } catch {}
    const url = `${base}?company_id=${encodeURIComponent(companyId)}`;
    return httpClient.get(url);
  }
}

export default new ProductsService();
